import type { ReactNode } from 'react';
import { Button, Typography } from '@mui/material';

import { IntakeJourneyShell, type IntakeJourneyStage } from './IntakeJourneyShell';
import { IntakeJourneyStatus } from './IntakeJourneyStatus';
import { IntakeKickoffCard } from './IntakeKickoffCard';

/** Only presentation availability is known here. Completion remains owned by each live feature. */
export function LiveIntakeJourney({
  enabled = true,
  selectedStage,
  onNavigate,
  intake,
  payers,
  payerEnabled,
  onKickoffClick,
}: {
  enabled?: boolean;
  selectedStage: IntakeJourneyStage;
  onNavigate: (stage: IntakeJourneyStage) => void;
  intake: ReactNode;
  payers: ReactNode;
  payerEnabled: boolean;
  onKickoffClick?: () => void;
}) {
  return (
    <IntakeJourneyShell
      showJourneyChrome={enabled}
      organizationName="Your practice"
      selectedStage={selectedStage}
      onNavigate={onNavigate}
      stages={{
        agreements: { statusLabel: 'Coming soon', caption: 'Services, agreements, and kickoff' },
        intake: { statusLabel: 'Available', caption: 'Practice, providers, and documents' },
        verification: { statusLabel: 'Coming soon', caption: 'JotPsych reviews your intake' },
        payers: payerEnabled
          ? { statusLabel: 'View current status', caption: 'Enrollment with each payer' }
          : { statusLabel: 'Unavailable', lockedReason: 'Payer connections are not enabled.' },
        import: { statusLabel: 'Coming soon', caption: 'Optional. Runs alongside the others' },
      }}
      kickoff={<IntakeKickoffCard onScheduleClick={onKickoffClick} />}
      content={{
        agreements: (
          <IntakeJourneyStatus
            title="Your agreements will appear here"
            statusLabel="DocuSign signing coming soon"
            action={
              <Button variant="contained" onClick={() => onNavigate('intake')}>
                Continue to intake
              </Button>
            }
          >
            <Typography>
              You’ll be able to review and sign your practice agreements here when signing is ready.
              You can continue your intake now.
            </Typography>
          </IntakeJourneyStatus>
        ),
        intake,
        verification: (
          <IntakeJourneyStatus
            title="Practice review status is coming soon"
            statusLabel="Not available yet"
            action={
              <Button variant="outlined" onClick={() => onNavigate('intake')}>
                Return to intake
              </Button>
            }
          >
            <Typography>
              Your existing intake and review tools remain available. This page does not yet show
              whether your practice has been verified.
            </Typography>
          </IntakeJourneyStatus>
        ),
        // Never instantiate a feature whose own access gate is disabled.
        payers: payerEnabled ? payers : null,
        // Placeholder until the EHR import surface lands in the hub (GDN-3733).
        import: (
          <IntakeJourneyStatus
            title="Import patients"
            statusLabel="Coming soon"
            action={
              <Button variant="outlined" onClick={() => onNavigate('intake')}>
                Return to intake
              </Button>
            }
          >
            <Typography>
              You’ll be able to upload patient records from your EHR here. Importing patients is
              optional, runs alongside the other steps, and does not affect your billing intake.
            </Typography>
          </IntakeJourneyStatus>
        ),
      }}
    />
  );
}
